import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/auth-context';
import { changePassword } from '../../modules/auth/services/changePassword.api';
import eyePasswordHideIcon from '../../assets/eye-password-hide-svgrepo-com.svg';
import eyePasswordShowIcon from '../../assets/eye-password-show-svgrepo-com.svg';
import { extractApiErrorMessage } from '../../core/api/errors';

// ── Password rules ──────────────────────────────────────────────────────────

const PASSWORD_RULES = [
  { label: 'At least 6 characters', test: (p: string) => p.length >= 6 },
  { label: 'Maximum 20 characters', test: (p: string) => p.length <= 20 },
  { label: 'Combination of upper- and lower-case characters', test: (p: string) => /[A-Z]/.test(p) && /[a-z]/.test(p) },
  { label: 'One or more digits', test: (p: string) => /\d/.test(p) },
  { label: 'At least one special character', test: (p: string) => /[^A-Za-z0-9]/.test(p) },
] as const;

const validatePassword = (p: string): string | null => {
  if (!p.trim()) return 'New password is required';
  if (!PASSWORD_RULES.every((r) => r.test(p)))
    return 'Password does not meet the required rules';
  return null;
};

const passwordFieldWrapperStyle: React.CSSProperties = {
  position: 'relative',
};

const passwordToggleStyle: React.CSSProperties = {
  position: 'absolute',
  right: '0.6rem',
  top: '50%',
  transform: 'translateY(-50%)',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  padding: 0,
  width: '1.25rem',
  height: '1.25rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

interface ChangePasswordModalProps {
  userId: string;
  userEmail?: string;
  onClose: () => void;
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  userId, userEmail, onClose,
}) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPwdTooltip, setShowPwdTooltip] = useState(false);
  const [errors, setErrors] = useState<{
    oldPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = () => {
    const e: typeof errors = {};
    if (!oldPassword.trim()) e.oldPassword = 'Current password is required';
    const pwErr = validatePassword(newPassword);
    if (pwErr) e.newPassword = pwErr;
    if (!confirmPassword.trim()) {
      e.confirmPassword = 'Please confirm your new password';
    } else if (newPassword !== confirmPassword) {
      e.confirmPassword = 'Passwords do not match';
    }
    return e;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setErrors({});
    setApiError(null);
    setIsSubmitting(true);

    try {
      const res = await changePassword({ userId, oldPassword, newPassword });
      if (!res.isSuccess) throw new Error(res.message || 'Password change failed.');

      setSuccessMsg((res.message || 'Password changed successfully.') + ' Please log in again.');
      await logout();
      onClose();
      navigate('/login', { replace: true });
    } catch (err: any) {
      setApiError(extractApiErrorMessage(err, 'Failed to change password. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPasswordToggleIcon = (isVisible: boolean) =>
    isVisible ? eyePasswordShowIcon : eyePasswordHideIcon;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        zIndex: 200, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '1rem',
      }}
    >
      <div
        style={{
          background: 'white', borderRadius: '10px', width: '100%',
          maxWidth: '420px', boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          background: '#1a3055', color: 'white', padding: '0.85rem 1.25rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <h2 style={{ fontSize: '0.95rem', fontWeight: 600, margin: 0 }}>
            Change Password
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.75)',
              cursor: 'pointer', fontSize: '1rem', width: '26px', height: '26px',
              borderRadius: '4px', display: 'flex', alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
              e.currentTarget.style.color = 'white';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'none';
              e.currentTarget.style.color = 'rgba(255,255,255,0.75)';
            }}
          >
            <i className="pi pi-times" style={{ fontSize: '1rem' }} />
          </button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); void handleSubmit(); }} noValidate>
          <input
            type="text"
            autoComplete="username"
            value={userEmail ?? userId}
            readOnly
            aria-hidden="true"
            style={{ display: 'none' }}
          />

          <div style={{ padding: '1.25rem', background: '#f8f9fa' }}>
            <div style={{
              background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px',
              padding: '1.1rem', display: 'flex', flexDirection: 'column', gap: '0.9rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}>
              {successMsg && (
                <div style={{
                  color: '#166534', background: '#dcfce7', border: '1px solid #86efac',
                  borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.85rem',
                }}>
                  {successMsg}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">
                  Current Password <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <div style={passwordFieldWrapperStyle}>
                  <input
                    className="form-input"
                    type={showOldPassword ? 'text' : 'password'}
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    placeholder="Enter current password"
                    autoComplete="current-password"
                    disabled={isSubmitting || !!successMsg}
                    style={{ paddingRight: '4.75rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowOldPassword((prev) => !prev)}
                    disabled={isSubmitting || !!successMsg}
                    style={passwordToggleStyle}
                    aria-label={showOldPassword ? 'Hide current password' : 'Show current password'}
                  >
                    <img
                      src={getPasswordToggleIcon(showOldPassword)}
                      alt=""
                      aria-hidden="true"
                      style={{ width: '1.1rem', height: '1.1rem' }}
                    />
                  </button>
                </div>
                {errors.oldPassword && (
                  <span className="form-error">{errors.oldPassword}</span>
                )}
              </div>

              <div className="form-group">
                {/* Label row with info tooltip */}
                <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.3rem' }}>
                  <label className="form-label" style={{ margin: 0 }}>
                    New Password <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <span
                    onMouseEnter={() => setShowPwdTooltip(true)}
                    onMouseLeave={() => setShowPwdTooltip(false)}
                    style={{ cursor: 'pointer', color: '#6366f1', display: 'inline-flex', alignItems: 'center' }}
                    aria-label="Password rules"
                  >
                    <i className="pi pi-info-circle" style={{ fontSize: '0.85rem' }} />
                  </span>
                  {showPwdTooltip && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, zIndex: 100,
                      marginTop: '0.4rem',
                      background: '#fff', border: '1px solid #d1d5db',
                      borderRadius: '8px', padding: '0.75rem 1rem',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                      width: '230px', pointerEvents: 'none',
                    }}>
                      <p style={{ margin: '0 0 0.4rem', fontWeight: 700, fontSize: '0.8rem', color: '#1a3055' }}>
                        Password Rules
                      </p>
                      <ul style={{ margin: 0, paddingLeft: '1.1rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                        {PASSWORD_RULES.map((r) => {
                          const passed = newPassword.length > 0 && r.test(newPassword);
                          return (
                            <li key={r.label} style={{
                              fontSize: '0.75rem', lineHeight: 1.45,
                              color: newPassword.length === 0 ? '#6b7280' : passed ? '#16a34a' : '#dc2626',
                            }}>
                              {r.label}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
                <div style={passwordFieldWrapperStyle}>
                  <input
                    className="form-input"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    autoComplete="new-password"
                    disabled={isSubmitting || !!successMsg}
                    style={{ paddingRight: '4.75rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword((prev) => !prev)}
                    disabled={isSubmitting || !!successMsg}
                    style={passwordToggleStyle}
                    aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                  >
                    <img
                      src={getPasswordToggleIcon(showNewPassword)}
                      alt=""
                      aria-hidden="true"
                      style={{ width: '1.1rem', height: '1.1rem' }}
                    />
                  </button>
                </div>
                {errors.newPassword && (
                  <span className="form-error">{errors.newPassword}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Confirm New Password <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <div style={passwordFieldWrapperStyle}>
                  <input
                    className="form-input"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                    disabled={isSubmitting || !!successMsg}
                    style={{ paddingRight: '4.75rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    disabled={isSubmitting || !!successMsg}
                    style={passwordToggleStyle}
                    aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                  >
                    <img
                      src={getPasswordToggleIcon(showConfirmPassword)}
                      alt=""
                      aria-hidden="true"
                      style={{ width: '1.1rem', height: '1.1rem' }}
                    />
                  </button>
                </div>
                {errors.confirmPassword && (
                  <span className="form-error">{errors.confirmPassword}</span>
                )}
              </div>

              {apiError && (
                <div style={{
                  color: '#dc2626', fontSize: '0.8rem', background: '#fef2f2',
                  border: '1px solid #fecaca', borderRadius: '6px',
                  padding: '0.5rem 0.75rem',
                }}>
                  {apiError}
                </div>
              )}
            </div>
          </div>

          <div style={{
            padding: '0.65rem 1.25rem 0.9rem', borderTop: '1px solid #e5e7eb',
            display: 'flex', justifyContent: 'flex-end', gap: '0.5rem',
            background: 'white',
          }}>
            <button type="button" className="btn btn--secondary" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={isSubmitting || !!successMsg}
            >
              {isSubmitting && (
                <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
              )}
              Change Password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


